import { Link } from "react-router-dom";
import {
  Beaker, Globe2, ShieldCheck, Truck, FileCheck2, Package, ArrowRight, CheckCircle2,
  FlaskConical, Award, Building2, TestTube2
} from "lucide-react";
import { COMPANY, IMAGES, BOTANICAL_EXTRACTS, BROCHURE_URL } from "@/lib/company";
import { openAndDownloadDossier } from "@/lib/download";
import TrustMarquee from "@/components/TrustMarquee";
import ContactForm from "@/components/ContactForm";

const whyChoose = [
  { icon: Building2, title: "Multiple Manufacturer Network", text: "We source from multiple GMP-certified facilities, ensuring you get the best quality at the best price — never limited to a single factory's capacity or pricing." },
  { icon: FileCheck2, title: "Full Export Compliance", text: "IEC, FSSAI Central License, GST registered. We handle all documentation: Commercial Invoices, Packing Lists, Certificates of Origin, and customs clearance." },
  { icon: ShieldCheck, title: "Quality Verification", text: "Pre-shipment quality checks, third-party CoA verification, and complete traceability documentation for every batch." },
  { icon: Truck, title: "Flexible Logistics", text: "FOB, CIF, and DDP terms available. Two+ years of hands-on customs clearing expertise ensuring seamless delivery." },
  { icon: Award, title: "Regulatory Support", text: "We understand TGA, USP, BP, and IP standards. Our documentation supports your regulatory submissions in Australia, Europe, and North America." },
  { icon: Package, title: "Small MOQ Friendly", text: "Start with 25 kg trial orders. We aggregate volumes across our network so you don't have to commit to factory-level minimums." },
];

const docs = [
  "Certificate of Analysis (CoA) — Batch-specific, ISO 17025 lab tested",
  "Manufacturer GMP Certificate",
  "Material Safety Data Sheet (MSDS)",
  "Certificate of Origin",
  "Commercial Invoice & Packing List",
  "FSSAI Export Compliance",
  "Non-GMO Statement",
  "Allergen-Free Statement",
  "BSE/TSE-Free Declaration",
  "Halal / Kosher Certificates (where applicable)",
  "Organic Certification (USDA / India Organic / OneCert — where applicable)",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroBg} alt="Botanical ingredients" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24 w-full">
          <div className="max-w-3xl fade-up">
            <span className="inline-block px-4 py-1.5 bg-brand-gold/95 text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-6">
              India → Global · Est. 2026
            </span>
            <h1 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Premium Botanical Extracts &<br />
              <span className="text-brand-gold">Agricultural Commodities</span>
              <span className="text-white/95"> — Sourced from India's Best, Delivered Globally.</span>
            </h1>
            <p className="mt-7 text-white/85 text-lg max-w-2xl leading-relaxed">
              {COMPANY.short} is your professional sourcing partner for GMP-certified herbal extracts, spices, and grains. We don't just trade — we curate quality.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-quote" className="btn-gold">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={BROCHURE_URL}
                download="Aad-Labs-Export-Dossier.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={openAndDownloadDossier}
                data-testid="hero-cta-brochure"
                className="btn-outline-white"
              >
                Download Product Brochure
              </a>
              <a href={BROCHURE_URL} download="Aad-Labs-Export-Dossier.pdf" target="_blank" rel="noopener noreferrer" onClick={openAndDownloadDossier} data-testid="hero-cta-portfolio" className="btn-outline-white">
                View Our Portfolio
              </a>
            </div>

            {/* Credentials strip */}
            <div className="mt-14 grid grid-cols-3 gap-4 max-w-2xl">
              {[
                { l: "IEC Code", v: COMPANY.iec },
                { l: "FSSAI", v: COMPANY.fssai },
                { l: "GSTIN", v: COMPANY.gstin.slice(0, 12) + "…" },
              ].map((c) => (
                <div key={c.l} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-md px-4 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-brand-gold font-bold">{c.l}</div>
                  <div className="text-white font-mono text-sm mt-1">{c.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <TrustMarquee />

      {/* ABOUT */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-7">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">About Aad Labs</span>
              <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline">
                We are not a manufacturer.<br />
                <span className="text-brand-green">We are something more valuable.</span>
              </h2>
              <div className="mt-8 space-y-5 text-brand-charcoal/85 leading-relaxed text-[15px]">
                <p>
                  <strong className="text-brand-charcoal">{COMPANY.name}</strong> is a Faridabad, Haryana-based export trading company specializing in premium botanical extracts, spices, and agricultural commodities. Founded by Director <strong>{COMPANY.director}</strong>, we leverage our deep network of WHO-GMP and ISO-certified manufacturers across India to deliver pharmaceutical-grade ingredients to global markets.
                </p>
                <p>Unlike single-factory agents, we maintain qualified relationships with multiple manufacturers. This allows us to:</p>
                <ul className="space-y-2 pl-1">
                  {[
                    "Select the optimal batch for every order based on real-time quality data",
                    "Ensure supply continuity and competitive pricing",
                    "Handle all export documentation, customs clearance, and logistics",
                    "Provide smaller MOQs by aggregating production across facilities",
                  ].map((it) => (
                    <li key={it} className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  With our IEC Code, FSSAI Central License, and GST registration, we are a fully compliant Indian export house ready to serve pharmaceutical companies, nutraceutical manufacturers, ingredient distributors, and food processors worldwide.
                </p>
              </div>
              <Link to="/about" data-testid="about-learn-more" className="mt-8 inline-flex items-center gap-2 text-brand-green font-semibold hover:text-brand-gold transition-colors">
                Learn more about our story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Credentials Box */}
            <div className="lg:col-span-5">
              <div className="bg-brand-green rounded-lg p-8 text-white shadow-xl border-t-4 border-brand-gold">
                <div className="text-xs uppercase tracking-widest text-brand-gold font-bold mb-6">Company Credentials</div>
                <div className="space-y-5">
                  {[
                    { l: "IEC Code", v: COMPANY.iec, sub: "Import Export Code, DGFT — Govt. of India" },
                    { l: "FSSAI Central License", v: COMPANY.fssai, sub: "Food Safety & Standards Authority of India" },
                    { l: "GSTIN", v: COMPANY.gstin, sub: "Haryana State Registration" },
                    { l: "PAN", v: COMPANY.pan, sub: "Corporate PAN — Income Tax India" },
                  ].map((c) => (
                    <div key={c.l} className="border-b border-white/15 pb-4 last:border-none last:pb-0">
                      <div className="text-brand-gold text-[11px] uppercase tracking-widest font-bold">{c.l}</div>
                      <div className="font-mono text-lg mt-1">{c.v}</div>
                      <div className="text-white/60 text-xs mt-0.5">{c.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section id="products" className="py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Our Products</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline-center">
              Standardized Botanical Extracts
            </h2>
            <p className="mt-5 text-brand-charcoal/75 leading-relaxed">
              Pharmaceutical-grade extracts standardized via HPLC & UV spectrophotometry, manufactured under WHO-GMP and ISO 22000:2018 standards, delivered with complete regulatory documentation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BOTANICAL_EXTRACTS.map((p) => (
              <div key={p.slug} data-testid={`product-card-${p.slug}`} className="product-card bg-white rounded-lg border border-neutral-200 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="text-xs font-semibold text-brand-gold italic mb-1 font-accent">{p.botanical}</div>
                  <h3 className="font-display font-bold text-brand-charcoal text-lg">{p.name}</h3>
                  <div className="mt-3 space-y-1 text-xs text-brand-charcoal/70">
                    <div><span className="font-semibold">Active:</span> {p.active}</div>
                    <div><span className="font-semibold">Standardization:</span> {p.standardization}</div>
                    <div><span className="font-semibold">Part:</span> {p.part}</div>
                  </div>
                  <Link to="/products/botanical-extracts" data-testid={`product-cta-${p.slug}`} className="mt-4 inline-flex items-center gap-1.5 text-brand-green text-sm font-semibold hover:text-brand-gold transition-colors">
                    Request Spec Sheet <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/products/botanical-extracts" data-testid="products-cta-botanical" className="group bg-brand-green rounded-lg p-8 flex items-center justify-between text-white hover:bg-brand-green-dark transition-colors">
              <div>
                <div className="text-brand-gold text-xs uppercase tracking-widest font-bold mb-2">Category A</div>
                <div className="font-display font-bold text-2xl">Botanical Extracts</div>
                <div className="text-white/70 text-sm mt-1">Ashwagandha · Bacopa · Turmeric</div>
              </div>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products/spices-seeds-grains" data-testid="products-cta-spices" className="group bg-[#212121] rounded-lg p-8 flex items-center justify-between text-white hover:bg-black transition-colors">
              <div>
                <div className="text-brand-gold text-xs uppercase tracking-widest font-bold mb-2">Category B & C</div>
                <div className="font-display font-bold text-2xl">Spices, Seeds & Grains</div>
                <div className="text-white/70 text-sm mt-1">Pepper · Cumin · Rice · Flax</div>
              </div>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products/coffee-beans" data-testid="products-cta-coffee" className="group rounded-lg p-8 flex items-center justify-between text-white transition-colors" style={{ background: "linear-gradient(135deg, #3a2416 0%, #5a3a1f 100%)" }}>
              <div>
                <div className="text-brand-gold text-xs uppercase tracking-widest font-bold mb-2">Category D</div>
                <div className="font-display font-bold text-2xl">Indian Coffee Beans</div>
                <div className="text-white/70 text-sm mt-1">Arabica · Robusta · Shade-grown</div>
              </div>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products/eco-packaging" data-testid="products-cta-packaging" className="group rounded-lg p-8 flex items-center justify-between text-white transition-colors" style={{ background: "linear-gradient(135deg, #124015 0%, #1B5E20 100%)" }}>
              <div>
                <div className="text-brand-gold text-xs uppercase tracking-widest font-bold mb-2">Category E</div>
                <div className="font-display font-bold text-2xl">Eco Packaging</div>
                <div className="text-white/70 text-sm mt-1">Compostable cups · Lids · Straws</div>
              </div>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUALITY */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <img src={IMAGES.lab} alt="Laboratory testing" className="rounded-lg shadow-xl w-full h-[520px] object-cover" />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Quality Assurance</span>
              <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline">
                Uncompromising<br />Quality Control
              </h2>
              <p className="mt-6 text-brand-charcoal/80 leading-relaxed">
                At Aad Labs, quality is not an afterthought — it is our foundation. We work exclusively with manufacturers who maintain stringent Quality Control & Assurance departments operating under ISO 22000:2005, WHO-GMP, and HACCP protocols.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  { icon: FlaskConical, label: "HPLC" },
                  { icon: TestTube2, label: "UV Spectrophotometer" },
                  { icon: Beaker, label: "TLC" },
                  { icon: Beaker, label: "GC" },
                  { icon: TestTube2, label: "Spectrometer" },
                  { icon: FlaskConical, label: "pH Meter" },
                ].map((t) => (
                  <div key={t.label} className="flex items-center gap-3 p-3 bg-brand-cream rounded-md border border-neutral-200">
                    <t.icon className="w-5 h-5 text-brand-green" />
                    <span className="text-sm font-semibold text-brand-charcoal">{t.label}</span>
                  </div>
                ))}
              </div>

              <Link to="/quality" data-testid="quality-learn-more" className="mt-8 inline-flex items-center gap-2 text-brand-green font-semibold hover:text-brand-gold transition-colors">
                Explore our full quality protocol <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Why Choose Us</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline-center">
              Six Reasons Global Buyers<br />Trust Aad Labs
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((w, i) => (
              <div key={i} data-testid={`why-choose-${i}`} className="bg-white p-8 rounded-lg border border-neutral-200 hover:border-brand-gold transition-colors">
                <div className="w-12 h-12 rounded-md bg-brand-green/10 flex items-center justify-center mb-5">
                  <w.icon className="w-6 h-6 text-brand-green" />
                </div>
                <h3 className="font-display font-bold text-brand-charcoal text-lg">{w.title}</h3>
                <p className="mt-3 text-sm text-brand-charcoal/75 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL REACH */}
      <section className="py-24 bg-brand-green text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Global Reach</span>
              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl gold-underline">
                Delivering Excellence Globally
              </h2>
              <p className="mt-6 text-white/85 leading-relaxed">
                From our base in Faridabad, India, we serve pharmaceutical manufacturers, nutraceutical brands, ingredient distributors, and food processors across <strong className="text-brand-gold">Australia, Europe, North America, and the Middle East</strong>. Our Star Export House network and SHEFEXIL membership ensure we meet the highest standards of international trade.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-4">
                {[
                  { n: "18+", l: "Certifications" },
                  { n: "4", l: "Continents Served" },
                  { n: "25 kg", l: "Minimum MOQ" },
                ].map((s) => (
                  <div key={s.l} className="border-l-2 border-brand-gold pl-4">
                    <div className="font-display font-bold text-3xl text-brand-gold">{s.n}</div>
                    <div className="text-white/70 text-xs uppercase tracking-widest mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <Globe2 className="w-full max-w-md h-auto text-brand-gold/40" strokeWidth={0.6} />
            </div>
          </div>
        </div>
      </section>

      {/* DOCUMENTATION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Documentation</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline-center">
              Documentation That Builds Trust
            </h2>
            <p className="mt-5 text-brand-charcoal/75">
              Every order ships with the complete documentation package your quality and regulatory teams need.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {docs.map((d) => (
              <div key={d} className="flex items-start gap-3 p-4 bg-brand-cream rounded-md border border-neutral-200">
                <FileCheck2 className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                <span className="text-sm text-brand-charcoal">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#212121] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight">
            Ready to Source Premium Botanical<br /> Extracts from India?
          </h2>
          <p className="mt-6 text-white/75 text-lg">
            Whether you need a 25 kg trial sample or a 500 kg recurring order, {COMPANY.short} is your reliable Indian sourcing partner.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-request-quote" className="btn-gold">Request a Quote</Link>
            <a href={BROCHURE_URL} download="Aad-Labs-Export-Dossier.pdf" target="_blank" rel="noopener noreferrer" onClick={openAndDownloadDossier} data-testid="cta-brochure" className="btn-outline-white">Download Product Brochure</a>
            <a href={`mailto:${COMPANY.email}?subject=Video Call Request`} data-testid="cta-video-call" className="btn-outline-white">Schedule a Video Call</a>
          </div>
        </div>
      </section>

      {/* CONTACT SNIPPET */}
      <section id="contact" className="py-24 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Contact Us</span>
              <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline">
                Let's build a partnership
              </h2>
              <p className="mt-6 text-brand-charcoal/80 leading-relaxed">
                Send us a quote request or product inquiry. Our team responds within one business day with pricing, specifications, and available batches.
              </p>

              <div className="mt-8 space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-widest text-brand-gold font-bold">Registered Address</div>
                  <div className="mt-1 text-brand-charcoal">{COMPANY.address}</div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-bold">Email</div>
                    <a data-testid="contact-info-email" href={`mailto:${COMPANY.email}`} className="mt-1 block text-brand-charcoal hover:text-brand-green">{COMPANY.email}</a>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-bold">Phone</div>
                    <a data-testid="contact-info-phone" href={`tel:${COMPANY.phoneRaw}`} className="mt-1 block text-brand-charcoal hover:text-brand-green">{COMPANY.phone}</a>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-brand-gold font-bold">Director</div>
                  <div className="mt-1 text-brand-charcoal">{COMPANY.director}</div>
                </div>
              </div>
            </div>
            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
